import React, { useState } from 'react';
import { Modal } from 'antd';
import { Info } from 'lucide-react';
import { getBucketColorConfig } from '../utils/bucketHelpers';

interface BucketColorLegendProps {
  buckets: {
    bucketCode: string;
    bucketLabel: string;
    sortOrder: number;
  }[];
}

/**
 * BucketColorLegend Component
 *
 * Displays a color-coded legend explaining the aging bucket color system
 * Can be shown as an info modal to help users understand the visual indicators
 */
export const BucketColorLegend: React.FC<BucketColorLegendProps> = ({
  buckets,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const bucketGroups = [
    {
      title: 'Safe Zone',
      icon: '🟢',
      description: 'Invoices not yet due - no immediate action required',
      codes: ['NOT_DUE'],
    },
    {
      title: 'Warning Zone',
      icon: '🟡',
      description: 'Invoices becoming due soon - prepare payment reminders',
      codes: ['GETTING_DUE'],
    },
    {
      title: 'Danger Zone',
      icon: '🔴',
      description: 'Overdue invoices - escalating action required',
      codes: [
        'OVERDUE_0_15',
        'OVERDUE_16_30',
        'OVERDUE_31_60',
        'OVERDUE_61_90',
        'OVERDUE_91_180',
        'OVERDUE_181_365',
        'OVERDUE_365_PLUS',
      ],
    },
  ];

  const actionGuide = {
    NOT_DUE: 'Monitor - No action needed',
    GETTING_DUE: 'Send advance payment notification',
    OVERDUE_0_15: 'Send gentle reminder',
    OVERDUE_16_30: 'Send formal reminder',
    OVERDUE_31_60: 'Phone call + formal notice',
    OVERDUE_61_90: 'Escalate to supervisor',
    OVERDUE_91_180: 'Management involvement required',
    OVERDUE_181_365: 'Legal notice consideration',
    OVERDUE_365_PLUS: 'Legal action / Write-off',
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-gray-300"
        title="View color legend and action guide"
      >
        <Info className="w-3.5 h-3.5" />
        Aging Guide
      </button>

      <Modal
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Info className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Aging Bucket Color Guide
              </h3>
              <p className="text-xs text-gray-500 font-normal">
                Understanding payment aging visual indicators
              </p>
            </div>
          </div>
        }
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        width={700}
      >
        <div className="py-4">
          {/* Quick Reference */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 mb-6">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3">
              Quick Reference
            </h4>
            <div className="flex items-center justify-around">
              <div className="text-center">
                <div className="text-3xl mb-1">🟢</div>
                <div className="text-xs font-semibold text-emerald-700">
                  Safe
                </div>
                <div className="text-[10px] text-gray-600">Not Due</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">🟡</div>
                <div className="text-xs font-semibold text-orange-700">
                  Warning
                </div>
                <div className="text-[10px] text-gray-600">Due Soon</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">🔴</div>
                <div className="text-xs font-semibold text-red-700">Danger</div>
                <div className="text-[10px] text-gray-600">Overdue</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">⚫</div>
                <div className="text-xs font-semibold text-gray-900">
                  Critical
                </div>
                <div className="text-[10px] text-gray-600">Very Overdue</div>
              </div>
            </div>
          </div>

          {/* Detailed Bucket Information */}
          <div className="space-y-4">
            {bucketGroups.map(group => {
              const groupBuckets = buckets.filter(b =>
                group.codes.includes(b.bucketCode),
              );

              if (groupBuckets.length === 0) return null;

              return (
                <div key={group.title}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{group.icon}</span>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">
                        {group.title}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {group.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 ml-8">
                    {groupBuckets
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map(bucket => {
                        const config = getBucketColorConfig(bucket.bucketCode);
                        const action =
                          actionGuide[
                            bucket.bucketCode as keyof typeof actionGuide
                          ] || 'Monitor';

                        return (
                          <div
                            key={bucket.bucketCode}
                            className={`${config.bg} border ${config.border} rounded-lg p-3`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: config.headerBg }}
                                  ></div>
                                  <span
                                    className={`text-sm font-semibold ${config.text}`}
                                  >
                                    {bucket.bucketLabel}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-gray-600">
                                    Recommended Action:
                                  </span>
                                  <span
                                    className={`font-medium ${config.text}`}
                                  >
                                    {action}
                                  </span>
                                </div>
                              </div>
                              <div
                                className={`px-2 py-1 rounded text-[10px] font-semibold uppercase ${config.text} ${config.bg} border ${config.border}`}
                              >
                                {config.severity}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Timeline Visual */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide mb-3">
              Aging Timeline
            </h4>
            <div className="relative">
              <div className="flex items-center">
                <div className="flex-1 h-8 bg-gradient-to-r from-emerald-400 via-orange-400 via-red-500 to-red-900 rounded-lg relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold mix-blend-difference">
                    ← Not Due &nbsp;&nbsp;&nbsp;&nbsp; Due Date
                    &nbsp;&nbsp;&nbsp;&nbsp; Overdue →
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] text-gray-600">
                <span>Future</span>
                <span>Today</span>
                <span>Past</span>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-2">
              💡 Key Insights
            </h4>
            <ul className="space-y-1 text-xs text-indigo-800">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span>Darker colors indicate higher severity and urgency</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span>
                  Click on any amount in the table to view detailed invoices
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span>
                  Dashboard at the top shows overall distribution at a glance
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 mt-0.5">•</span>
                <span>
                  Use bulk actions to send reminders to multiple customers
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
};
